import {
    Component,
    Input,
    OnInit,
    TemplateRef,
    ViewChild,
} from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";

import {
    Product,
    ProductTemplateType,
    ProductVariants,
} from "@hermes/api-model-product";
import { hasSizeVariant } from "@hermes/fragments/product-utils";
import { ProductVariant } from "@hermes/fragments/variant-item";
import {
    BUCKLE,
    LEATHER,
    ProductPageFacade,
    ProductVariantType,
    SelectedVariantVariantsState,
    SIZE,
} from "@hermes/states/product-page";
export interface VariantTemplateMapping {
    template: TemplateRef<HTMLElement>;
    accessibilityTemplate?: TemplateRef<HTMLElement>;
}
export type ExtractedProductVariantType = Extract<
    ProductVariantType,
    "buckle" | "leather"
>;
// TODO update union type if a product contains more variants

export type TemplateVariantMap = Record<
    ExtractedProductVariantType,
    VariantTemplateMapping
>;

@Component({
    selector: "h-variant-template",
    templateUrl: "./variant-template.component.html",
    styleUrl: "./variant-template.component.scss",
})
export class VariantTemplateComponent implements OnInit {
    @Input()
    public selectedProductVariant!: SelectedVariantVariantsState;

    @Input()
    public variantsForm: FormGroup = new FormGroup({});

    @Input()
    public hideSizeAlert: boolean = false;

    @Input()
    public findInStorePressed: boolean = false;

    @ViewChild("buckle", { static: true })
    public buckleTemplate!: TemplateRef<HTMLElement>;

    @ViewChild("buckleAccessibility", { static: true })
    public buckleAccessibilityTemplate!: TemplateRef<HTMLElement>;

    @ViewChild("leather", { static: true })
    public leatherTemplate!: TemplateRef<HTMLElement>;

    public variantType!: ProductVariantType;

    public templateVariantMap!: TemplateVariantMap;

    public isProductRevampSelectorEnabled: boolean = false;

    private inputProduct!: Product;

    constructor(private readonly productPageFacade: ProductPageFacade) {}

    public get product() {
        return this.inputProduct;
    }

    @Input()
    public set product(product: Product) {
        this.inputProduct = product;
        this.isProductRevampSelectorEnabled =
            this.productPageFacade.isProductRevampSelectorEnabled(product);
    }

    public ngOnInit(): void {
        this.templateVariantMap = {
            buckle: {
                template: this.buckleTemplate,
                accessibilityTemplate: this.buckleAccessibilityTemplate,
            },
            leather: { template: this.leatherTemplate },
        };
    }

    public getNumberOfLinesToDisplay(): number {
        const limitNumberOfVariants = 2;
        const maxTotalLines = 2; // to display on two lines if number of variants is lower or equal to 2
        const minTotalLines = 1; // to display on one line if number of variants is higher than 2
        const variants: ProductVariants | undefined =
            this.inputProduct.variants;
        if (!variants) {
            return 0;
        }

        const numberOfVariants = Object.keys(variants).filter(
            (key) => variants[key as keyof ProductVariants] !== undefined,
        ).length;

        return numberOfVariants <= limitNumberOfVariants
            ? maxTotalLines
            : minTotalLines;
    }

    public getSelectedValue(variantType: ProductVariantType): string {
        switch (variantType) {
            case BUCKLE: {
                return this.selectedProductVariant?.buckle?.skuBuckle ?? "";
            }
            case LEATHER: {
                return this.selectedProductVariant?.leather?.refColoris ?? "";
            }
            // TODO add more cases if a product contains other variants
            default: {
                throw new Error(`Unknown variant type: ${variantType}`);
            }
        }
    }

    /**
     * Check is the Size Alert should be shown
     */
    public displaySizeAlert(): boolean {
        const sizeVariantsForm: AbstractControl | null =
            this.variantsForm.get(SIZE);
        return (
            !!sizeVariantsForm &&
            sizeVariantsForm.invalid &&
            hasSizeVariant(this.product) &&
            !this.hideSizeAlert
        );
    }

    public hasMessageError(): boolean {
        return (
            (this.variantsForm.invalid || this.findInStorePressed) &&
            this.displaySizeAlert()
        );
    }

    public handleSizeSelection(): void {
        if (this.product.variants) {
            this.productPageFacade.showMoreVariantsRequested({
                variantItems: [this.product.variants.sizes],
                productType: ProductTemplateType.Simple,
            });
        }
    }

    public getVariantType(variantKey: string): ProductVariantType {
        return variantKey.slice(0, -1) as ProductVariantType;
    }

    public getVariants(variantType: string): ProductVariant[] {
        const key = `${variantType}s` as keyof ProductVariants;
        return this.product.variants
            ? (this.product.variants[key] as ProductVariant[])
            : [];
    }

    public getTemplateForVariantType(
        variantType: ExtractedProductVariantType,
    ): VariantTemplateMapping {
        return this.templateVariantMap[variantType];
    }
}
